import React from "react";
import { Users, Bot, Coins, Ticket, ShieldAlert, Calendar } from "lucide-react";
import useFetch from "@/lib/useFetch";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatCard from "@/components/common/StatCard";
import { themeFor } from "@/lib/communities";

export default function Analytics() {
    const { data } = useFetch("/admin/analytics");
    if (!data) return <Loader />;
    const maxScrap = Math.max(1, ...data.per_community.map((c) => c.scrap_total));

    return (
        <div data-testid="admin-analytics">
            <PageHeader title="Analytics" subtitle="Live network metrics across all communities." />
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                <StatCard icon={Users} label="Members" value={data.total_users} accent="#FF6B00" testId="an-members" />
                <StatCard icon={Bot} label="Discord Linked" value={`${data.discord_connected_pct}%`} accent="#5865F2" sub={`${data.discord_connected} users`} testId="an-discord" />
                <StatCard icon={Coins} label="Transactions" value={data.transactions} accent="#F59E0B" testId="an-txns" />
                <StatCard icon={Calendar} label="Events" value={data.events} accent="#00FF66" testId="an-events" />
                <StatCard icon={ShieldAlert} label="Punishments" value={data.punishments} accent="#EF4444" testId="an-punishments" />
                <StatCard icon={Ticket} label="Clans" value={data.clans} accent="#D9480F" testId="an-clans" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <SectionCard title="Economy by Community" testId="an-economy-chart">
                    <div className="space-y-4">
                        {data.per_community.map((c) => {
                            const t = themeFor({ theme: c.theme });
                            return (
                                <div key={c.key}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-heading uppercase text-foreground">{c.name}</span>
                                        <span className="font-mono text-muted-foreground">{c.scrap_total.toLocaleString()} scrap</span>
                                    </div>
                                    <div className="h-3 rounded-full bg-background overflow-hidden">
                                        <div className="h-full rounded-full transition-all" style={{ width: `${(c.scrap_total / maxScrap) * 100}%`, background: t.accent }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </SectionCard>

                <SectionCard title="Community Breakdown" testId="an-breakdown">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="text-left label-mono text-muted-foreground border-b border-border">
                                <th className="p-2">Community</th><th className="p-2 text-right">Online</th><th className="p-2 text-right">Events</th><th className="p-2 text-right">Clans</th><th className="p-2 text-right">Open Tickets</th>
                            </tr></thead>
                            <tbody>
                                {data.per_community.map((c) => (
                                    <tr key={c.key} className="border-b border-border/60">
                                        <td className="p-2 text-foreground">{c.name}</td>
                                        <td className="p-2 text-right text-radioactive font-heading font-bold">{c.players_online}</td>
                                        <td className="p-2 text-right text-foreground">{c.events}</td>
                                        <td className="p-2 text-right text-foreground">{c.clans}</td>
                                        <td className="p-2 text-right text-foreground">{c.tickets_open}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        {Object.entries(data.tickets_by_status).map(([k, v]) => (
                            <div key={k} className="rounded-md border border-border bg-background/40 p-3 text-center">
                                <div className="font-heading text-2xl font-bold text-foreground">{v}</div>
                                <div className="label-mono text-muted-foreground">{k.replace("_", " ")}</div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            </div>
            <Footer />
        </div>
    );
}
